import { DEFAULT_STRAP_DESIGN, sourceKey, strapTemplateHasDesign, transformAt, type EditProject, type EditSegment, type TextFont } from '@airpost/shared';
import { lookPaint, motionAt, noise, placePicture } from './edit-look.js';
import { overlayDraws, strapDraws, textDraws, type Anim, type FrameSpec, type Layer, type OverlayDraw, type StrapDraw, type TextDraw } from './edit-plan.js';
import { drawStrapArt, frameTakesDesign, strapArt, type StrapArt, type StrapStamp } from './strap-art.js';

/**
 * Drawing an edit's frame: the monitor and the export both paint with the same `FrameRenderer`, so the preview is
 * what is written. A frame is a background, the pieces (each painted on its own sheet: the ground around it, its
 * picture cropped, fitted, framed and moving, its colour, its effects; then laid on the frame with its transition's
 * alpha, wipe or push), the pictures over the sequence, the texts, then the straps in the station's design for the
 * edit (ADR 083); a hidden track is not drawn. Sizes are relative to the frame, so a 9:16 or a 1080p frame looks like the
 * 720p monitor. Each piece, picture and text drawn leaves its box (centre, size, turn) for the monitor's handles.
 */
export interface RenderResult {
  blob: Blob;
  mime: string;
  durationMs: number;
}

/** What a layer is drawn from: a video element, a decoded frame, a photo. */
export interface LayerImage {
  img: CanvasImageSource;
  w: number;
  h: number;
}
/** Where a piece, a text or a picture over the sequence was drawn, in the canvas's pixels: its unturned box and its turn about its centre. */
export interface Box {
  kind: 'piece' | 'text' | 'overlay';
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Degrees, about the box's centre. */
  rot: number;
}
/** Whether a point of the canvas is inside a (turned) box. */
export function inBox(b: Box, px: number, py: number): boolean {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const a = (-b.rot * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const lx = dx * Math.cos(a) - dy * Math.sin(a);
  const ly = dx * Math.sin(a) + dy * Math.cos(a);
  return Math.abs(lx) <= b.w / 2 && Math.abs(ly) <= b.h / 2;
}

export const FONT_FAMILIES: Record<TextFont, string> = {
  sans: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  condensed: '"Roboto Condensed", "Arial Narrow", "Segoe UI Semibold", sans-serif',
  display: '"Arial Black", "Segoe UI Black", Impact, sans-serif',
  mono: 'Consolas, "Courier New", monospace',
};

type Ctx = CanvasRenderingContext2D;
function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

let filterOk: boolean | null = null;
/** Whether the browser's canvas applies CSS filters (Chrome, Edge, Firefox; not every Safari): without them a look keeps only its tints and vignette. */
export function canvasFilters(): boolean {
  if (filterOk === null) {
    try {
      const c = makeCanvas(1, 1).getContext('2d');
      if (!c || !('filter' in c)) filterOk = false;
      else {
        c.filter = 'blur(1px)';
        filterOk = c.filter === 'blur(1px)';
      }
    } catch {
      filterOk = false;
    }
  }
  return filterOk;
}

export interface RenderInput {
  project: EditProject;
  tMs: number;
  spec: FrameSpec;
  /** A piece's picture now (a video frame), or null while it loads. */
  imageFor(layer: Layer): LayerImage | null;
  /** A photo by its source key (`image:…`), or null while it loads. */
  stillFor(key: string): LayerImage | null;
  /** A live preview (the monitor): a design's clock shows the time now; a file for air leaves it out. */
  live?: boolean;
}

/** Lines of words that fit `maxW`, from paragraphs split at the journalist's line breaks. */
function wrapLines(c: Ctx, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) {
      out.push('');
      continue;
    }
    let line = '';
    for (const w of words) {
      const next = line ? `${line} ${w}` : w;
      if (line && c.measureText(next).width > maxW) {
        out.push(line);
        line = w;
      } else line = next;
    }
    out.push(line);
  }
  return out;
}

export class FrameRenderer {
  readonly w: number;
  readonly h: number;
  /** 1 at a 720 short side. */
  private readonly unit: number;
  private readonly sheet: HTMLCanvasElement;
  private readonly sc: Ctx;
  private readonly pic: HTMLCanvasElement;
  private readonly pc: Ctx;
  private readonly tiny: HTMLCanvasElement;
  private readonly tc: Ctx;
  private grain: HTMLCanvasElement | null = null;
  /** Where the pieces, pictures and texts of the last frame were drawn, topmost last. */
  boxes: Box[] = [];

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.unit = Math.min(w, h) / 720;
    this.sheet = makeCanvas(w, h);
    this.sc = this.sheet.getContext('2d')!;
    this.pic = makeCanvas(w, h);
    this.pc = this.pic.getContext('2d')!;
    this.tiny = makeCanvas(48, Math.max(8, Math.round((48 * h) / w)));
    this.tc = this.tiny.getContext('2d')!;
  }

  /** A frame; `graphicsOnly` draws only what lies over the pictures (pictures, texts, straps) on a clear canvas: the graphics the playout lays over its render. */
  render(c: Ctx, inp: RenderInput, opts: { graphicsOnly?: boolean } = {}): void {
    const { w, h } = this;
    const p = inp.project;
    c.save();
    c.globalAlpha = 1;
    c.globalCompositeOperation = 'source-over';
    if (opts.graphicsOnly) c.clearRect(0, 0, w, h);
    else {
      c.fillStyle = inp.spec.bg === 'white' ? '#ffffff' : '#000000';
      c.fillRect(0, 0, w, h);
    }
    const frameNo = Math.floor(inp.tMs / 40);
    const hidden = new Set(p.lanes?.hidden ?? []);
    this.boxes = [];
    for (const l of hidden.has('video') || opts.graphicsOnly ? [] : inp.spec.layers) {
      if (l.alpha <= 0.001) continue;
      const seg = p.segments[l.index];
      if (!seg) continue;
      const im = seg.source.kind === 'image' ? inp.stillFor(sourceKey(seg.source)) : inp.imageFor(l);
      this.paintSheet(p, seg, l, im, frameNo);
      c.save();
      c.globalAlpha = Math.min(1, l.alpha);
      if (l.clipX0 > 0 || l.clipX1 < 1) {
        c.beginPath();
        c.rect(l.clipX0 * w, 0, Math.max(0, (l.clipX1 - l.clipX0) * w), h);
        c.clip();
      }
      let ox = l.dx * w;
      let oy = 0;
      let grow = 0;
      const shake = seg.effects.find((e) => e.type === 'shake');
      if (shake && shake.amount > 0) {
        const amp = shake.amount * 0.02 * Math.min(w, h);
        ox += (noise(frameNo) - 0.5) * 2 * amp;
        oy += (noise(frameNo + 7.1) - 0.5) * 2 * amp;
        grow = shake.amount * 0.05;
      }
      c.drawImage(this.sheet, ox - (w * grow) / 2, oy - (h * grow) / 2, w * (1 + grow), h * (1 + grow));
      const glitch = seg.effects.find((e) => e.type === 'glitch');
      // a glitch: 120 ms in every 600, slices of the picture slip sideways
      if (glitch && glitch.amount > 0 && Math.floor(Math.max(0, l.localMs) / 40) % 15 < 3) {
        for (let k = 0; k < 6; k++) {
          const y = noise(frameNo * 7 + k) * h;
          const sh = (0.02 + noise(frameNo * 13 + k) * 0.07) * h;
          const shift = (noise(frameNo * 17 + k) - 0.5) * 2 * glitch.amount * 0.08 * w;
          c.drawImage(this.sheet, 0, y, w, sh, ox + shift, oy + y, w, sh);
        }
      }
      c.restore();
    }
    if (!hidden.has('overlay')) for (const d of overlayDraws(p, inp.tMs)) this.drawOverlay(c, d, inp.stillFor(sourceKey(d.overlay.source)));
    if (!hidden.has('text')) for (const d of textDraws(p, inp.tMs)) this.drawText(c, d);
    if (!hidden.has('straps')) drawStraps(c, strapDraws(p, inp.tMs), w, h, strapArt(p.strapDesign ?? DEFAULT_STRAP_DESIGN), { now: new Date(), clock: !!inp.live });
    c.restore();
  }

  /** One piece on its sheet: the ground, the picture with its look and frame, the vignette and the effects. */
  private paintSheet(p: EditProject, seg: EditSegment, l: Layer, im: LayerImage | null, frameNo: number): void {
    const { w, h, sc, pc } = this;
    const f = p.format;
    sc.save();
    sc.globalAlpha = 1;
    sc.globalCompositeOperation = 'source-over';
    if (f.background === 'blur' && im && im.w && im.h) {
      // the picture itself, very small and stretched over the frame: a blur that costs nothing
      const tw = this.tiny.width;
      const th = this.tiny.height;
      const s = Math.max(tw / im.w, th / im.h);
      this.tc.drawImage(im.img, (tw - im.w * s) / 2, (th - im.h * s) / 2, im.w * s, im.h * s);
      sc.imageSmoothingEnabled = true;
      sc.imageSmoothingQuality = 'high';
      if (canvasFilters()) sc.filter = `blur(${Math.round(6 * this.unit)}px)`;
      sc.drawImage(this.tiny, -w * 0.05, -h * 0.05, w * 1.1, h * 1.1);
      sc.filter = 'none';
      sc.fillStyle = 'rgba(0,0,0,0.3)';
      sc.fillRect(0, 0, w, h);
    } else {
      sc.fillStyle = f.background === 'color' ? f.color : '#000000';
      sc.fillRect(0, 0, w, h);
    }
    sc.restore();
    if (!im || !im.w || !im.h) return;
    const look = lookPaint(seg.look, this.unit);
    pc.clearRect(0, 0, w, h);
    pc.save();
    if (look.css !== 'none' && canvasFilters()) pc.filter = look.css;
    const q = l.lengthMs > 0 ? l.localMs / l.lengthMs : 0;
    // the frame at this moment of the piece: its keyframes applied
    const tr = transformAt(seg, Math.max(0, l.localMs));
    const pl = placePicture(im.w, im.h, w, h, f.fit, tr, motionAt(seg.motion, q));
    this.boxes.push({ kind: 'piece', id: seg.id, x: pl.cx + l.dx * w - pl.dw / 2, y: pl.cy - pl.dh / 2, w: pl.dw, h: pl.dh, rot: tr.rotation });
    pc.translate(pl.cx, pl.cy);
    pc.rotate(pl.rotation);
    pc.scale(pl.flipH ? -1 : 1, pl.flipV ? -1 : 1);
    pc.drawImage(im.img, pl.sx, pl.sy, pl.sw, pl.sh, -pl.dw / 2, -pl.dh / 2, pl.dw, pl.dh);
    pc.restore();
    if (look.washes.length) {
      // tints over the picture only, not the ground around it
      pc.save();
      pc.globalCompositeOperation = 'source-atop';
      for (const wa of look.washes) {
        pc.globalAlpha = wa.alpha;
        pc.fillStyle = wa.color;
        pc.fillRect(0, 0, w, h);
      }
      pc.restore();
    }
    sc.save();
    sc.globalAlpha = tr.opacity;
    sc.drawImage(this.pic, 0, 0);
    sc.restore();
    if (look.vignette > 0.001) {
      const r = Math.hypot(w, h) / 2;
      const g = sc.createRadialGradient(w / 2, h / 2, r * (0.55 - 0.25 * look.vignette), w / 2, h / 2, r);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, `rgba(0,0,0,${(0.85 * look.vignette).toFixed(3)})`);
      sc.fillStyle = g;
      sc.fillRect(0, 0, w, h);
    }
    for (const e of seg.effects) {
      if (e.amount <= 0) continue;
      if (e.type === 'grain') {
        const tile = this.grainTile();
        const pat = sc.createPattern(tile, 'repeat');
        if (!pat) continue;
        sc.save();
        sc.globalCompositeOperation = 'overlay';
        sc.globalAlpha = 0.45 * e.amount;
        sc.translate(-Math.floor(noise(frameNo) * tile.width), -Math.floor(noise(frameNo + 3.3) * tile.height));
        sc.fillStyle = pat;
        sc.fillRect(0, 0, w + tile.width, h + tile.height);
        sc.restore();
      } else if (e.type === 'bars') {
        const bh = h * 0.125 * e.amount;
        sc.fillStyle = '#000000';
        sc.fillRect(0, 0, w, bh);
        sc.fillRect(0, h - bh, w, bh);
      } else if (e.type === 'flash' && l.localMs >= 0 && l.localMs < 400) {
        sc.fillStyle = `rgba(255,255,255,${(e.amount * (1 - l.localMs / 400)).toFixed(3)})`;
        sc.fillRect(0, 0, w, h);
      }
    }
  }

  /** A tile of noise, the same every time (so the export's grain is the monitor's). */
  private grainTile(): HTMLCanvasElement {
    if (this.grain) return this.grain;
    const size = Math.max(64, Math.round(160 * this.unit));
    const tile = makeCanvas(size, size);
    const ctx = tile.getContext('2d')!;
    const img = ctx.createImageData(size, size);
    for (let i = 0; i < size * size; i++) {
      const v = Math.round(noise(i * 1.37) * 255);
      img.data[i * 4] = v;
      img.data[i * 4 + 1] = v;
      img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    this.grain = tile;
    return tile;
  }

  private drawOverlay(c: Ctx, d: OverlayDraw, im: LayerImage | null): void {
    if (!im || !im.w || !im.h) return;
    const { w, h } = this;
    const o = d.overlay;
    const ow = o.width * w * d.anim.scale;
    const oh = (ow * im.h) / im.w;
    const cx = o.x * w + d.anim.dx * w;
    const cy = o.y * h + d.anim.dy * h;
    c.save();
    c.globalAlpha = Math.max(0, Math.min(1, o.opacity * d.anim.alpha));
    c.translate(cx, cy);
    c.rotate((o.rotation * Math.PI) / 180);
    if (d.anim.wipe < 1) {
      c.beginPath();
      c.rect(-ow / 2, -oh / 2, ow * d.anim.wipe, oh);
      c.clip();
    }
    c.drawImage(im.img, -ow / 2, -oh / 2, ow, oh);
    c.restore();
    this.boxes.push({ kind: 'overlay', id: o.id, x: cx - ow / 2, y: cy - oh / 2, w: ow, h: oh, rot: o.rotation });
  }

  /**
   * A text: its first paragraph at its size, the rest at `subScale` of it (not bold), wrapped at its width; on its
   * box, with its decoration in the accent colour (a bar at the left, a line under it, the first line on a label of
   * its own, a frame); turned, faded, wiped and typed as its keyframes and animation say.
   */
  private drawText(c: Ctx, d: TextDraw): void {
    const { w, h } = this;
    const s = d.style;
    const px = Math.max(6, (s.size / 100) * Math.min(w, h));
    const font = (size: number, bold: boolean) => `${s.italic ? 'italic ' : ''}${bold ? '700' : '400'} ${size.toFixed(1)}px ${FONT_FAMILIES[s.font]}`;
    const words = s.uppercase ? d.text.text.toLocaleUpperCase('el') : d.text.text;
    const sub = s.subScale < 0.999;
    c.save();
    const lines: Array<{ text: string; px: number; bold: boolean; w: number }> = [];
    words.split('\n').forEach((para, i) => {
      const lp = i > 0 && sub ? px * s.subScale : px;
      const lb = i > 0 && sub ? false : s.bold;
      c.font = font(lp, lb);
      for (const line of wrapLines(c, para, s.width * w)) lines.push({ text: line, px: lp, bold: lb, w: c.measureText(line).width });
    });
    const deco = s.accent ? s.deco : 'none';
    const blockW = Math.max(1, ...lines.map((l) => l.w));
    const lh = (l: { px: number }) => l.px * 1.22;
    const textH = lines.reduce((n, l) => n + lh(l), 0);
    const framed = !!s.bg || deco !== 'none';
    const padX = framed ? px * 0.45 : px * 0.1;
    const padY = framed ? px * 0.22 : px * 0.05;
    const bar = deco === 'bar' ? px * 0.18 : 0;
    const bw = blockW + padX * 2 + bar;
    const bh = textH + padY * 2 + (deco === 'underline' ? px * 0.2 : 0);
    const cx = s.x * w + d.anim.dx * w;
    const cy = s.y * h + d.anim.dy * h;
    const alpha = Math.max(0, Math.min(1, d.anim.alpha * s.opacity));
    c.translate(cx, cy);
    c.rotate((s.rotation * Math.PI) / 180);
    c.scale(d.anim.scale, d.anim.scale);
    const left = -bw / 2;
    const top = -bh / 2;
    if (d.anim.wipe < 1) {
      c.beginPath();
      c.rect(left - px, top - px, (bw + px * 2) * d.anim.wipe, bh + px * 2);
      c.clip();
    }
    const box = (x: number, y: number, bw2: number, bh2: number, radius: number) => {
      c.beginPath();
      if (typeof c.roundRect === 'function' && radius > 0) c.roundRect(x, y, bw2, bh2, radius);
      else c.rect(x, y, bw2, bh2);
      c.fill();
    };
    const first = lines[0];
    const tagH = first && deco === 'tag' ? padY * 1.5 + lh(first) : 0;
    if (s.bg) {
      c.globalAlpha = alpha * s.bgOpacity;
      c.fillStyle = s.bg;
      // a news design keeps square corners; a plain box is softened
      box(left, top + tagH, bw, bh - tagH, deco === 'none' ? px * 0.16 : 0);
    }
    if (deco !== 'none' && s.accent) {
      c.globalAlpha = alpha;
      c.fillStyle = s.accent;
      c.strokeStyle = s.accent;
      if (deco === 'bar') c.fillRect(left, top, bar, bh);
      else if (deco === 'underline') c.fillRect(left + padX, top + padY + textH + px * 0.06, blockW, px * 0.09);
      else if (deco === 'frame') {
        c.lineWidth = px * 0.07;
        c.strokeRect(left + c.lineWidth / 2, top + c.lineWidth / 2, bw - c.lineWidth, bh - c.lineWidth);
      } else if (deco === 'tag' && first) c.fillRect(left, top, first.w + padX * 2, tagH);
    }
    c.globalAlpha = alpha;
    c.textBaseline = 'middle';
    // a label's lines start at the left; the others as aligned
    const align = deco === 'tag' ? 'left' : s.align;
    c.textAlign = align;
    const x0 = left + bar + padX;
    const tx = align === 'left' ? x0 : align === 'right' ? x0 + blockW : x0 + blockW / 2;
    // the typewriter: as many characters as are typed by now, line after line
    let leftChars = d.anim.reveal >= 1 ? Infinity : Math.round(lines.reduce((n, l) => n + l.text.length, 0) * d.anim.reveal);
    let y = top + padY + (deco === 'tag' ? padY * 0.5 : 0);
    lines.forEach((line) => {
      const ty = y + lh(line) / 2;
      y += lh(line);
      if (leftChars <= 0) return;
      const shown = leftChars === Infinity ? line.text : line.text.slice(0, leftChars);
      if (leftChars !== Infinity) leftChars -= line.text.length;
      c.font = font(line.px, line.bold);
      if (s.shadow && !s.bg) {
        c.shadowColor = 'rgba(0,0,0,0.7)';
        c.shadowBlur = line.px * 0.14;
        c.shadowOffsetY = line.px * 0.04;
      }
      if (s.outline) {
        c.lineJoin = 'round';
        c.lineWidth = line.px * 0.14;
        c.strokeStyle = '#000000';
        c.strokeText(shown, tx, ty);
        c.shadowColor = 'transparent';
      }
      c.fillStyle = s.color;
      c.fillText(shown, tx, ty);
      c.shadowColor = 'transparent';
    });
    c.restore();
    const sw = bw * d.anim.scale;
    const shh = bh * d.anim.scale;
    this.boxes.push({ kind: 'text', id: d.text.id, x: cx - sw / 2, y: cy - shh / 2, w: sw, h: shh, rot: s.rotation });
  }
}

/** Paint in an animation's state, over the strip of the frame a strap takes: its alpha, its shift, its size about its centre, the wipe from its left. */
function animated(c: CanvasRenderingContext2D, a: Anim, w: number, h: number, area: { x: number; y: number; w: number; h: number }, paint: () => void): void {
  if (a.alpha <= 0.001 || a.wipe <= 0) return;
  c.save();
  c.globalAlpha *= a.alpha;
  if (a.dx || a.dy) c.translate(a.dx * w, a.dy * h);
  if (a.scale !== 1) {
    const cx = area.x + area.w / 2;
    const cy = area.y + area.h / 2;
    c.translate(cx, cy);
    c.scale(a.scale, a.scale);
    c.translate(-cx, -cy);
  }
  if (a.wipe < 1) {
    c.beginPath();
    c.rect(area.x, 0, area.w * a.wipe, h);
    c.clip();
  }
  paint();
  c.restore();
}

/**
 * The straps over a frame, each in its animation (ADR 089). In a 16:9 frame with the design's picture loaded, the
 * latest lower third shows in the station's design (a new one takes the old one's place, as on air); otherwise each
 * strap is AirPost's schematic, a blue bar at the bottom left with the first line bold, scaled to the frame.
 */
export function drawStraps(c: CanvasRenderingContext2D, draws: StrapDraw[], w: number, h: number, art: StrapArt | null = null, stamp: StrapStamp = { now: new Date(), clock: false }): void {
  const designed = art && frameTakesDesign(w, h) ? draws.filter((d) => strapTemplateHasDesign(d.strap.template)) : [];
  const latest = designed[designed.length - 1];
  if (art && latest) {
    const k = w / 1920;
    const r = art.design.area;
    animated(c, latest.anim, w, h, { x: r.x * k, y: r.y * k, w: r.w * k, h: r.h * k }, () => drawStrapArt(c, art, latest.strap.lines, w, h, stamp));
  }
  const u = Math.min(w, h) / 720;
  const tall = h > w * 0.9;
  draws.filter((d) => !designed.includes(d)).forEach(({ strap: s, anim }, i) => {
    const two = s.lines.length >= 2 && !!s.lines[1]?.trim();
    const bh = (two ? 92 : 64) * u;
    const y = h * (tall ? 0.8 : 0.9) - bh - i * (bh + 12 * u);
    const x = w * 0.06;
    const bw = w * (tall ? 0.88 : 0.6);
    const g = c.createLinearGradient(x, 0, x + bw, 0);
    g.addColorStop(0, 'rgba(45, 95, 147, 0.96)');
    g.addColorStop(0.7, 'rgba(31, 74, 120, 0.96)');
    g.addColorStop(1, 'rgba(31, 74, 120, 0.55)');
    animated(c, anim, w, h, { x, y, w: bw, h: bh }, () => {
    c.save();
    c.fillStyle = g;
    c.fillRect(x, y, bw, bh);
    c.fillStyle = '#ff9f1c';
    c.fillRect(x, y, 8 * u, bh);
    c.fillStyle = '#fff';
    c.textBaseline = 'middle';
    c.textAlign = 'left';
    c.font = `700 ${Math.round(34 * u)}px system-ui, "Segoe UI", Roboto, Arial, sans-serif`;
    c.fillText((s.lines[0] ?? '').toUpperCase(), x + 26 * u, two ? y + 32 * u : y + bh / 2, bw - 40 * u);
    if (two) {
      c.font = `400 ${Math.round(24 * u)}px system-ui, "Segoe UI", Roboto, Arial, sans-serif`;
      c.fillStyle = 'rgba(255,255,255,0.92)';
      c.fillText(s.lines[1] ?? '', x + 26 * u, y + 68 * u, bw - 40 * u);
    }
    c.restore();
    });
  });
}

export const RENDER_MIMES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
/** The container the browser can record (the fallback when it has no WebCodecs), or null when it cannot record at all. */
export const renderMime = (): string | null => (typeof MediaRecorder === 'undefined' ? null : (RENDER_MIMES.find((m) => MediaRecorder.isTypeSupported(m)) ?? null));
